import dayjs from "dayjs";
import { useForecastContext } from "../../../../../context/forecastContext";
import SkeletonLoader from "../../../../SkeletonLoader";
import { useTheme } from "../../../../../context/themeContext";
import ErrorMessage from "../../../../errorMessage/errorMessage";
import { publicHolidaysForDate } from "../../../../../utilities/publicHolidayHelpers";
import { CalendarBlankLined, CalendarUserLined } from "@tag/tag-icons";
import { Badge, Pill, Pills, Popover } from "@tag/tag-components-react-v4";
import "./monthDayEvents.css";
import { usePublicHolidayQuery } from "../../../../../services/nationalHolidayService";
import { useSiteEventsQuery } from "../../../../../services/siteEventsService";
import { filterSiteEventsForDate } from "../../../../../utilities/siteEventsHelpers";

type MonthDayEventsProps = {
  day: dayjs.Dayjs;
};

const MonthDayEvents: React.FC<MonthDayEventsProps> = ({ day }) => {
  const { forecastState } = useForecastContext();
  const { theme } = useTheme();
  const {
    data: publicHolidays,
    isLoading: isPublicHolsLoading,
    isError: isPublicHolidayError,
  } = usePublicHolidayQuery({
    searchPeriod: forecastState.searchPeriod,
    country: "GB",
  });
  const {
    data: siteEvents,
    isLoading: isSiteEventsLoading,
    isError: isSiteEventsError,
  } = useSiteEventsQuery({
    siteId: forecastState.selectedSite?.siteId!,
    searchPeriod: forecastState.searchPeriod,
  });

  if (isPublicHolsLoading || isSiteEventsLoading) {
    return <SkeletonLoader />;
  }

  if (isPublicHolidayError || isSiteEventsError) {
    return <ErrorMessage dataType="Public Holidays" fontSize="11px" />;
  }

  const siteEventsForDate = filterSiteEventsForDate(day.toDate(), siteEvents);
  const publicHols = publicHolidaysForDate(publicHolidays ?? [], day);

  return (
    <div className="month-day-events-container">
      <div className="month-day-site-events-info">
        <Popover
          popoverFor={
            <div className="month-day-site-events-info-icon">
              <CalendarBlankLined accent="teal" />
              {publicHols.length > 0 && (
                <div className="week-day-site-events-info-badge">
                  <Badge>{publicHols.length}</Badge>
                </div>
              )}
            </div>
          }
          background={theme}
          style={{
            borderRadius: "8px",
            border: "1px solid var(--border-color)",
          }}
        >
          {publicHols.length > 0 ? (
            publicHols.map((holiday, index) => (
              <div
                className="month-day-public-holidays-region-popover-content"
                key={index}
              >
                <div className="month-day-public-holidays-region-popover-header">
                  {holiday.localName}
                </div>

                {`${holiday.localName} is a public holiday in the following regions:`}

                {holiday.subDivionCodes && holiday.subDivionCodes.length > 0 ? (
                  <Pills>
                    {holiday.subDivisionNames?.map((item, index) => (
                      <Pill key={index}>{item}</Pill>
                    ))}
                  </Pills>
                ) : (
                  <Pills>
                    <Pill>{holiday.countryName}</Pill>
                  </Pills>
                )}
              </div>
            ))
          ) : (
            <div className="month-day-no-site-events-container">
              <span>{"No public holidays"}</span>
            </div>
          )}
        </Popover>
        <Popover
          popoverFor={
            <div className="month-day-site-events-info-icon">
              <CalendarUserLined accent="teal" />
              {siteEventsForDate && siteEventsForDate.length > 0 && (
                <div className="week-day-site-events-info-badge">
                  <Badge>{siteEventsForDate.length}</Badge>
                </div>
              )}
            </div>
          }
          background={theme}
          style={{
            borderRadius: "8px",
            border: "1px solid var(--border-color)",
          }}
        >
          {siteEventsForDate && siteEventsForDate.length > 0 ? (
            siteEventsForDate.map((siteEvent, index) => (
              <div
                key={index}
                className="month-day-site-events-popover-content"
              >
                <div className="month-day-site-events-popover-header">
                  {siteEvent.name}
                </div>
                {siteEvent.description}
                <Pills>
                  {siteEvent.tags.map((tag, index) => (
                    <Pill key={index}>{tag}</Pill>
                  ))}
                </Pills>
              </div>
            ))
          ) : (
            <div className="month-day-no-site-events-container">
              <span>{"No site events"}</span>
            </div>
          )}
        </Popover>
      </div>
    </div>
  );
};

export default MonthDayEvents;
